import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  screenshareStateMachine,
  INITIAL_SCREENSHARE_STATE,
} from './screenshare.xstate';
import {
  ScreenshareState,
  type ScreensharePayload,
  type ScreenshareInitialState,
} from './screenshare.types';
import { interpret } from 'xstate';

const initialState: ScreenshareInitialState = {
  screenshareState: INITIAL_SCREENSHARE_STATE,
  currentRequestedMemberId: undefined,
  currentEventData: undefined,
  startedAt: undefined,
};

const screenshareStateMachineInstance = interpret(
  screenshareStateMachine,
).start();

export const extractCurrentState = (states: string[]): ScreenshareState => {
  const stateValue = states[states.length - 1] ?? '';
  const stateChain = stateValue.split('.');
  const subState = stateChain[stateChain.length - 1] as ScreenshareState;
  return subState;
};

// internal function to aid testing
export const _resetScreenshare =
  (machineInstance: typeof screenshareStateMachineInstance) => () => {
    machineInstance.stop();
    machineInstance.start();
    return reset();
  };

export const resetScreenShare = _resetScreenshare(
  screenshareStateMachineInstance,
);

export const screenshareSlice = createSlice({
  name: 'screenshareState',
  initialState,
  reducers: {
    /**
     * Triggers a state transition in the state machine.
     * @param state - The current state of the slice.
     * @param action - The dispatched action.
     */
    setTransition: (state, action: PayloadAction<ScreensharePayload>) => {
      const currentStateValue = state.screenshareState;
      const nextState = screenshareStateMachineInstance.send(action.payload);
      const nextStateValue = extractCurrentState(nextState.toStrings());
      state.currentEventData = action.payload.eventData ?? undefined;
      state.screenshareState = nextStateValue;

      // only set startedAt when transitioning to InProgress. Ideally the reducer would be a pure function.
      if (
        currentStateValue !== ScreenshareState.InProgress &&
        nextStateValue === ScreenshareState.InProgress
      ) {
        state.startedAt = Date.now();
      }

      if (nextStateValue !== ScreenshareState.InProgress) {
        state.startedAt = undefined;
      }
    },
    setCurrentRequestedMember: (
      state,
      action: PayloadAction<
        ScreenshareInitialState['currentRequestedMemberId']
      >,
    ) => {
      state.currentRequestedMemberId = action.payload;
    },
    reset: () => {
      return {
        ...initialState,
      };
    },
  },
});

export const { setTransition, setCurrentRequestedMember, reset } =
  screenshareSlice.actions;
